// -------------------------------
// GOAL TYPE INTERFACES
// -------------------------------
export interface PendingGoalType {
  goalType?: string;
  description?: string;
  status?: string;
}

export interface PendingDeleteGoalType {
  awaitingName?: boolean;
}

export const defaultGoalTypeStatus = "active";

/**
 * Start CREATE Goal Type flow
 */
export const startCreateGoalTypeFlow = (
  setPendingGoalType: (data: PendingGoalType | null) => void,
  pushAIChat: (msg: string) => void,
  speak: (msg: string) => void,
  nameFromIntent?: string
) => {
  if (nameFromIntent) {
    setPendingGoalType({
      goalType: nameFromIntent,
      status: defaultGoalTypeStatus
    });

    pushAIChat(
      `Got it! Creating goal type "${nameFromIntent}". Should it be Active or Inactive?`
    );
    speak(`Creating goal type ${nameFromIntent}. Should it be active?`);
    return;
  }

  setPendingGoalType({});
  pushAIChat("What should the goal type be called?");
  speak("What should the goal type be called?");
};

/**
 * Continue CREATE Goal Type flow
 */
export const processPendingGoalType = (
  text: string,
  pendingGoalType: PendingGoalType,
  setPendingGoalType: (data: PendingGoalType | null) => void,
  pushAIChat: (msg: string) => void,
  speak: (msg: string) => void
) => {

  // STEP 1 — NAME
  if (!pendingGoalType.goalType) {
    const cleanName = text.trim();

    if (!cleanName) {
      pushAIChat("Please provide a valid goal type name.");
      speak("Please provide a valid goal type name.");
      return;
    }

    setPendingGoalType({ ...pendingGoalType, goalType: cleanName });

    pushAIChat("Would you like to add a description?");
    speak("Would you like to add a description?");
    return;
  }

  // STEP 2 — DESCRIPTION
  if (pendingGoalType.description === undefined) {
    setPendingGoalType({
      ...pendingGoalType,
      description: text || ""
    });

    pushAIChat("Should this goal type be Active or Inactive?");
    speak("Should this goal type be active or inactive?");
    return;
  }

  // STEP 3 — STATUS (FINAL)
  if (!pendingGoalType.status) {
    const status = text.toLowerCase().includes("inactive")
      ? "inactive"
      : "active";

    pushAIChat(
      `Creating goal type "${pendingGoalType.goalType}" with status ${status}...`
    );
    speak(`Creating goal type ${pendingGoalType.goalType}`);

    window.dispatchEvent(
      new CustomEvent("ai-create-goaltype", {
        detail: {
          Goaltype: pendingGoalType.goalType,
          Description: pendingGoalType.description,
          Status: status
        }
      })
    );

    setPendingGoalType(null);
  }
};

/**
 * Start DELETE Goal Type flow
 */
export const startDeleteGoalTypeFlow = (
  setPendingDelete: (data: PendingDeleteGoalType | null) => void,
  pushAIChat: (msg: string) => void,
  speak: (msg: string) => void,
  nameFromIntent?: string
) => {
  if (nameFromIntent) {
    pushAIChat(`Deleting goal type "${nameFromIntent}"...`);
    speak(`Deleting goal type ${nameFromIntent}`);

    window.dispatchEvent(
      new CustomEvent("ai-delete-goaltype", {
        detail: { name: nameFromIntent }
      })
    );
    return;
  }

  setPendingDelete({ awaitingName: true });
  pushAIChat("Which goal type do you want to delete?");
  speak("Which goal type do you want to delete?");
};

/**
 * Continue DELETE Goal Type flow
 */
export const processPendingDeleteGoalType = (
  text: string,
  pendingDelete: PendingDeleteGoalType,
  setPendingDelete: (data: PendingDeleteGoalType | null) => void,
  pushAIChat: (msg: string) => void,
  speak: (msg: string) => void
) => {
  if (pendingDelete.awaitingName) {
    const cleanName = text.trim();

    if (!cleanName) {
      pushAIChat("Please provide a valid goal type name.");
      speak("Please provide a valid goal type name.");
      return;
    }

    pushAIChat(`Deleting goal type "${cleanName}"...`);
    speak(`Deleting goal type ${cleanName}`);

    window.dispatchEvent(
      new CustomEvent("ai-delete-goaltype", {
        detail: { name: cleanName }
      })
    );

    setPendingDelete(null);
  }
};
